import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import {
  useGqlMutation,
  useGqlQuery,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { createWorkOrderDriver } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderDriverMutations";
import {
  CreateWorkOrderDriverInput,
  Currency,
  ExpensePaidBy,
  ExpensePaidTo,
  ExpenseType,
  TaxRegistration,
} from "@/gql/graphql";
import { z } from "zod";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { ProDriverInput } from "@/app/ProDrivers/Drivers/Components/ProDriverInput";
import { FormDivider } from "@/lib/Components/Form/Components/FormDivider";
import { WorkOrderAssignmentDetailsForm } from "@/app/ProDrivers/WorkOrders/Components/Forms/WorkOrderAssignmentDetailsForm";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { useWorkOrder } from "@/app/ProDrivers/WorkOrders/Hooks/useWorkOrder";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { SelectInput } from "@/lib/Components/Form/Inputs/SelectInput";
import { proDriverAgreementTemplateListQuery } from "@/app/ProDrivers/Agreements/GraphQL/agreementTemplateQueries";
import { AgreementTemplateInput } from "@/app/ProDrivers/Agreements/Components/AgreementTemplateInput";
import React from "react";
import { FieldArray, FieldArrayRenderProps, useFormikContext } from "formik";
import { Button } from "@/components/catalyst/button";
import { PlusIcon } from "@heroicons/react/24/solid";
import { TrashIcon } from "@heroicons/react/24/outline";
import { proDriverRecordQuery } from "@/app/ProDrivers/Drivers/GraphQL/proDriverRecordQuery";
import { InfoBanner } from "@/lib/Components/Banners/InfoBanner";
import { addTax, getTaxRate } from "@/app/Payments/Utils/taxRates";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";

const validation = z.object({
  workOrder: z.object({
    connect: z.string(),
  }),
  proDriver: z.object({
    connect: z.string(),
  }),
  expenses: z
    .array(
      z.object({
        type: z.nativeEnum(ExpenseType),
        paid_by: z.nativeEnum(ExpensePaidBy),
        paid_to: z.nativeEnum(ExpensePaidTo),
        amount: z.number().min(1, "Amount is required"),
        description: z.string().optional(),
      }),
    )
    .optional(),
});

interface AssignmentInitialValues {
  proDriverId?: string | null;
  pickupDate?: string | null;
  deliveryDate?: string | null;
  driverNotes?: string | null;
}

interface CreateWorkOrderAssignmentDialogProps {
  workOrderId: string;
  initialValues?: AssignmentInitialValues;
}

export function CreateWorkOrderAssignmentDialog({
  workOrderId,
  initialValues,
}: CreateWorkOrderAssignmentDialogProps) {
  const { close, resolve } = useDialogState();
  const { mutateAsync } = useGqlMutation(createWorkOrderDriver);
  const workOrder = useWorkOrder(workOrderId);
  const defaultCurrency = workOrder.currency;
  const taxRegistrationRef = React.useRef<TaxRegistration>(
    TaxRegistration.None,
  );
  const { data: templateData } = useSuspenseGqlQuery(
    proDriverAgreementTemplateListQuery,
    {},
  );
  const defaultTemplate = templateData.proDriverAgreementTemplates.find(
    (template) => template.is_default,
  );

  return (
    <BaseDialog
      title="Create Assignment"
      subtitle="Add the driver and movement details for this work order"
    >
      <GenericForm<CreateWorkOrderDriverInput>
        initialValues={{
          workOrder: {
            connect: workOrderId,
          },
          proDriver: {
            connect: initialValues?.proDriverId ?? null!,
          },
          pickup_date:
            initialValues?.pickupDate ?? workOrder.depart_after ?? null,
          pickup_time: null,
          delivery_date:
            initialValues?.deliveryDate ?? workOrder.deliver_before ?? null,
          delivery_time: null,
          driver_notes:
            initialValues?.driverNotes ?? workOrder.description ?? null,
          agreement_template_id: defaultTemplate?.id ?? null,
          expenses: [
            {
              type: ExpenseType.DriverPay,
              paid_by: ExpensePaidBy.Imoova,
              paid_to: ExpensePaidTo.Driver,
              amount: null!,
              description: "",
              currency: defaultCurrency,
            },
            {
              type: ExpenseType.Fuel,
              paid_by: ExpensePaidBy.Driver,
              paid_to: ExpensePaidTo.Driver,
              amount: null!,
              description: "",
              currency: defaultCurrency,
            },
          ],
        }}
        validationSchema={toFormikValidationSchema(validation)}
        onSubmit={async (values) => {
          const taxReg = taxRegistrationRef.current;
          const input = {
            ...values,
            expenses: (values.expenses ?? []).map((e) => ({
              ...e,
              currency: defaultCurrency,
              amount:
                e.type === ExpenseType.DriverPay
                  ? addTax(e.amount, taxReg)
                  : e.amount,
            })),
          };

          await mutateAsync({
            input,
          });

          await close();

          resolve?.({});
        }}
      >
        <ProDriverInput
          name="proDriver.connect"
          label="Pro Driver"
          className="col-span-full"
          autoFocus={!initialValues?.proDriverId}
        />

        <AgreementTemplateInput
          name="agreement_template_id"
          label="Agreement"
          description="Sent to the driver as soon as they are assigned. Leave empty to skip."
          className="col-span-full"
          clearable
        />

        <FormDivider />

        <WorkOrderAssignmentDetailsForm />

        <FormDivider />

        <ExpensesFieldArray
          currency={defaultCurrency}
          taxRegistrationRef={taxRegistrationRef}
        />

        <DialogSubmitButton text="Add Driver" />
      </GenericForm>
    </BaseDialog>
  );
}

type ExpenseLineItem = {
  type: ExpenseType;
  paid_by: ExpensePaidBy;
  paid_to: ExpensePaidTo;
  amount: number | null;
  description: string;
};

function getExpenseCategoryLabel(expense: ExpenseLineItem): string {
  if (expense.type === ExpenseType.DriverPay) {
    return "Driver Pay";
  }
  if (expense.paid_by === ExpensePaidBy.Driver) {
    return "Driver Expense";
  }
  return "Imoova Expense";
}

function SelectedDriverTaxNotice({
  taxRegistrationRef,
}: {
  taxRegistrationRef: React.MutableRefObject<TaxRegistration>;
}) {
  const { values } = useFormikContext<{
    proDriver: { connect: string | null };
  }>();
  const proDriverId = values.proDriver?.connect;

  const { data } = useGqlQuery(
    proDriverRecordQuery,
    { id: proDriverId! },
    { enabled: !!proDriverId },
  );

  const taxRegistration =
    data?.proDriver?.tax_registration ?? TaxRegistration.None;

  React.useEffect(() => {
    taxRegistrationRef.current = taxRegistration;
  }, [taxRegistration, taxRegistrationRef]);

  if (!proDriverId || !data) {
    return null;
  }

  if (taxRegistration === TaxRegistration.None) {
    return null;
  }

  return (
    <div className="col-span-full mb-2">
      <InfoBanner>
        {`This driver is registered for ${taxRegistration.replace("_", " ")}. Tax will be added to driver pay.`}
      </InfoBanner>
    </div>
  );
}

function ExpensesFieldArray({
  currency,
  taxRegistrationRef,
}: {
  currency: Currency;
  taxRegistrationRef: React.MutableRefObject<TaxRegistration>;
}) {
  const { values } = useFormikContext<{ expenses: ExpenseLineItem[] }>();
  const expenses = values.expenses ?? [];
  const hasDriverPay = expenses.some((e) => e.type === ExpenseType.DriverPay);

  return (
    <FieldArray name="expenses">
      {(fieldHelpers: FieldArrayRenderProps) => (
        <div className="col-span-full">
          <SelectedDriverTaxNotice taxRegistrationRef={taxRegistrationRef} />
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-medium text-gray-800">Expenses</h2>
            <div className="flex flex-wrap gap-1">
              <Button
                type="button"
                plain
                className="max-sm:gap-x-1 max-sm:px-1.5 max-sm:py-0.5 max-sm:text-xs/5"
                disabled={hasDriverPay}
                onClick={async () => {
                  fieldHelpers.push({
                    type: ExpenseType.DriverPay,
                    paid_by: ExpensePaidBy.Imoova,
                    paid_to: ExpensePaidTo.Driver,
                    amount: null,
                    description: "",
                  });
                }}
              >
                <PlusIcon className="size-4" data-slot="icon" />
                Driver Pay
              </Button>
              <Button
                type="button"
                plain
                className="max-sm:gap-x-1 max-sm:px-1.5 max-sm:py-0.5 max-sm:text-xs/5"
                onClick={async () => {
                  fieldHelpers.push({
                    type: ExpenseType.Fuel,
                    paid_by: ExpensePaidBy.Driver,
                    paid_to: ExpensePaidTo.Driver,
                    amount: null,
                    description: "",
                  });
                }}
              >
                <PlusIcon className="size-4" data-slot="icon" />
                Driver Expense
              </Button>
              <Button
                type="button"
                plain
                className="max-sm:gap-x-1 max-sm:px-1.5 max-sm:py-0.5 max-sm:text-xs/5"
                onClick={async () => {
                  fieldHelpers.push({
                    type: ExpenseType.Ferry,
                    paid_by: ExpensePaidBy.Imoova,
                    paid_to: ExpensePaidTo.ThirdParty,
                    amount: null,
                    description: "",
                  });
                }}
              >
                <PlusIcon className="size-4" data-slot="icon" />
                Imoova Expense
              </Button>
            </div>
          </div>

          {expenses.length > 0 ? (
            <div className="shadow-xs overflow-hidden rounded-xl border border-gray-200 bg-white">
              <ul className="divide-y divide-gray-100">
                {expenses.map((item, index) => (
                  <ExpenseLineItemRow
                    key={index}
                    item={item}
                    index={index}
                    currency={currency}
                    fieldHelpers={fieldHelpers}
                    taxRegistrationRef={taxRegistrationRef}
                  />
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      )}
    </FieldArray>
  );
}

function ExpenseLineItemRow({
  item,
  index,
  currency,
  fieldHelpers,
  taxRegistrationRef,
}: {
  item: ExpenseLineItem;
  index: number;
  currency: Currency;
  fieldHelpers: FieldArrayRenderProps;
  taxRegistrationRef: React.MutableRefObject<TaxRegistration>;
}) {
  const isDriverPay = item.type === ExpenseType.DriverPay;
  const isDriverExpense = item.paid_by === ExpensePaidBy.Driver;
  const categoryLabel = getExpenseCategoryLabel(item);
  const taxRate = getTaxRate(taxRegistrationRef.current);
  const hasTax = isDriverPay && taxRate > 0;

  const typeOptions = isDriverExpense
    ? [
        { label: "Fuel", value: ExpenseType.Fuel },
        { label: "Ferry", value: ExpenseType.Ferry },
        { label: "Flight", value: ExpenseType.Flight },
        { label: "Accommodation", value: ExpenseType.Accommodation },
        { label: "Transportation", value: ExpenseType.Transportation },
        { label: "Other", value: ExpenseType.Other },
      ]
    : [
        { label: "Ferry", value: ExpenseType.Ferry },
        { label: "Flight", value: ExpenseType.Flight },
        { label: "Accommodation", value: ExpenseType.Accommodation },
        { label: "Transportation", value: ExpenseType.Transportation },
        { label: "Other", value: ExpenseType.Other },
      ];

  const amount = item.amount ?? 0;
  const tax = Math.round(amount * taxRate);
  const incTax = amount + tax;

  return (
    <li className="group transition-colors hover:bg-gray-50">
      <div className="flex items-center p-3 text-xs font-semibold uppercase tracking-wider text-gray-600">
        <div className="flex grow items-center space-x-4">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100 text-indigo-600">
            {index + 1}
          </span>
          <span>{categoryLabel}</span>
        </div>
        <button
          type="button"
          className="rounded p-1 text-gray-400 opacity-0 hover:text-red-500 group-hover:opacity-100"
          onClick={() => fieldHelpers.remove(index)}
        >
          <TrashIcon className="size-4" />
        </button>
      </div>
      <div className="p-4">
        <div className="grid w-full grid-cols-3 gap-6 lg:grid-cols-12">
          {isDriverPay ? (
            <>
              <CurrencyInput
                name={`expenses.${index}.amount`}
                label={hasTax ? "Amount (exc. tax)" : "Amount"}
                className="col-span-full lg:col-span-4"
                currency={currency}
              />
              {hasTax && amount > 0 ? (
                <p className="col-span-full self-end pb-2 text-sm text-gray-500 lg:col-span-8">
                  Tax: {formatCurrency(tax, currency)} · Total inc. tax:{" "}
                  {formatCurrency(incTax, currency)}
                </p>
              ) : null}
            </>
          ) : (
            <>
              <SelectInput
                name={`expenses.${index}.type`}
                label="Type"
                className="col-span-3 lg:col-span-3"
                options={[{ label: "", options: typeOptions }]}
              />
              <CurrencyInput
                name={`expenses.${index}.amount`}
                label="Amount"
                className="col-span-3 lg:col-span-3"
                currency={currency}
              />
              <TextInput
                name={`expenses.${index}.description`}
                label="Description"
                className="col-span-full lg:col-span-6"
              />
            </>
          )}
        </div>
      </div>
    </li>
  );
}
